// frontend/app/components/lessonPlans/authoring/BlockAssetRefField.js
//
// The three reference field kinds — 3D scene, video segment and topic quiz.
// Split out of BlockEditor because picking real curriculum content is its own
// problem: everything else in a block is typed, this is chosen.

import React, { useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import LessonAssetPicker from "./LessonAssetPicker";
import { useLessonAuthoring } from "./LessonAuthoringContext";
import { Field, TextField } from "./WizardFields";
import { FIELD } from "./blockFields";

/**
 * Human-readable name for a chosen ref, looked up in the topic catalogue.
 *
 * `extraRows` ({ pwos, videos }) are rows browsed from other topics this
 * session. A match found only there is named but still `known: false`, so the
 * field flags it as "from another topic".
 */
export const describeRef = (kind, ref, catalog, t, extraRows) => {
  if (!ref) return null;
  const lookup = t || ((key) => key);
  const translate = (key, params = {}) =>
    lookup(key).replace(/\{(\w+)\}/g, (match, token) =>
      Object.prototype.hasOwnProperty.call(params, token) ? String(params[token]) : match
    );

  if (kind === FIELD.PWO_REF) {
    if (!ref.source_pwo_id || !ref.scene_name) return null;
    const samePwo = (row) =>
      row.source_pwo_id === ref.source_pwo_id && row.scene_name === ref.scene_name;
    const inLesson = (catalog?.pwos || []).find(samePwo);
    const match = inLesson || (extraRows?.pwos || []).find(samePwo);
    return {
      title: match ? match.scene_label : ref.scene_name,
      meta: match
        ? `${match.pwo_label} · ${ref.scene_name}`
        : translate("PWO {id} · {scene}", { id: ref.source_pwo_id, scene: ref.scene_name }),
      known: Boolean(inLesson),
      fromOtherTopic: Boolean(match) && !inLesson,
    };
  }

  if (!ref.source_video_id) return null;
  const sameVideo = (row) =>
    row.source_video_id === ref.source_video_id &&
    (row.tag_id ?? null) === (ref.tag_id ?? null);
  const inLesson = (catalog?.videos || []).find(sameVideo);
  const match = inLesson || (extraRows?.videos || []).find(sameVideo);
  return {
    title: match ? match.tag_label : translate("Video {id}", { id: ref.source_video_id }),
    meta: match
      ? `${match.video_label}${match.tag_start_time ? ` · ${match.tag_start_time}` : ""}`
      : ref.tag_id
        ? translate("Video {id} · tag {tag}", { id: ref.source_video_id, tag: ref.tag_id })
        : translate("Video {id} · whole video", { id: ref.source_video_id }),
    known: Boolean(inLesson),
    fromOtherTopic: Boolean(match) && !inLesson,
  };
};

/**
 * Asset references are chosen from the topic's real catalogue, so an author
 * never has to know a scene's internal name.
 *
 * Manual entry stays behind a toggle: if the catalogue call fails an author who
 * knows the ids should not be blocked. Picking from another topic is supported
 * too (the picker can browse the whole curriculum): the runtime resolver falls
 * back to a board-wide search, see backend/services/lesson_asset_resolver.py.
 */
const AssetRefField = ({ colors, block, kind, onPatch, boardTopicId, catalog, t }) => {
  const translate = t || ((key) => key);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manual, setManual] = useState(false);

  const ref = block.asset_ref || {};
  const setRef = (patch) => onPatch({ asset_ref: { ...ref, ...patch } });
  const asNumber = (value) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : trimmed;
  };

  // The topic quiz has nothing to browse — it is always this lesson's topic.
  if (kind === FIELD.TOPIC_QUIZ_REF) {
    return (
      <Field
        colors={colors}
        label={translate("Topic id")}
        hint={translate("Defaults to this lesson's topic.")}
      >
        <TextField
          colors={colors}
          value={ref.board_topic_id ?? boardTopicId ?? ""}
          onChange={(v) => setRef({ board_topic_id: asNumber(v) })}
          placeholder={boardTopicId ? String(boardTopicId) : ""}
          keyboardType="numeric"
        />
      </Field>
    );
  }

  const isPwo = kind === FIELD.PWO_REF;
  const authoring = useLessonAuthoring();
  const chosen = describeRef(kind, ref, catalog, t, authoring?.browsedRows);

  return (
    <Field
      colors={colors}
      label={translate(isPwo ? "3D scene" : "Video segment")}
      required
    >
      <View
        style={[
          styles.chosen,
          {
            backgroundColor: colors.subtleBg,
            borderColor: chosen ? colors.border : colors.teacherTipBorder,
          },
        ]}
      >
        <MaterialCommunityIcons
          name={isPwo ? "cube-outline" : "play-circle-outline"}
          size={18}
          color={chosen ? colors.accentPrimary : colors.textMuted}
        />

        <View style={{ flex: 1, minWidth: 0 }}>
          {chosen ? (
            <>
              <Text
                style={[styles.chosenTitle, { color: colors.textPrimary }]}
                numberOfLines={1}
              >
                {chosen.title}
              </Text>
              <Text
                style={[styles.chosenMeta, { color: colors.textMuted }]}
                numberOfLines={1}
              >
                {chosen.meta}
                {/* The catalogue is scoped to this lesson's topic; a ref outside
                    it is a supported cross-topic pick, flagged so a typo'd id
                    still does not read as a resolved scene. */}
                {chosen.known ? "" : ` · ${translate("from another topic")}`}
              </Text>
            </>
          ) : (
            <Text style={[styles.chosenEmpty, { color: colors.textMuted }]}>
              {translate("Nothing chosen yet")}
            </Text>
          )}
        </View>

        <TouchableOpacity
          onPress={() => setPickerOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={
            isPwo ? translate("Choose a 3D scene") : translate("Choose a video segment")
          }
          style={[styles.chooseButton, { borderColor: colors.accentPrimary }]}
        >
          <Text style={[styles.chooseText, { color: colors.accentPrimary }]}>
            {chosen ? translate("Change") : translate("Choose")}
          </Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        onPress={() => setManual(!manual)}
        accessibilityRole="button"
        accessibilityLabel={translate("Enter ids manually")}
        style={styles.manualToggle}
      >
        <Text style={[styles.manualText, { color: colors.textMuted }]}>
          {manual ? translate("Hide manual entry") : translate("Enter ids manually")}
        </Text>
      </TouchableOpacity>

      {manual ? (
        <View style={styles.refRow}>
          <View style={styles.refItem}>
            <Field colors={colors} label={translate(isPwo ? "PWO id" : "Video id")}>
              <TextField
                colors={colors}
                value={isPwo ? ref.source_pwo_id : ref.source_video_id}
                onChange={(v) =>
                  setRef(
                    isPwo
                      ? { source_pwo_id: asNumber(v) }
                      : { source_video_id: asNumber(v) }
                  )
                }
                placeholder={isPwo ? translate("PWO id") : translate("Video id")}
                keyboardType="numeric"
              />
            </Field>
          </View>
          <View style={styles.refItem}>
            <Field
              colors={colors}
              label={translate(isPwo ? "Scene name" : "Tag id")}
              hint={isPwo ? undefined : translate("Leave blank to play the whole video.")}
            >
              <TextField
                colors={colors}
                value={isPwo ? ref.scene_name : ref.tag_id}
                onChange={(v) =>
                  setRef(
                    isPwo
                      ? { scene_name: String(v || "").trim() }
                      : {
                          tag_id: asNumber(v),
                          is_full_video: String(v ?? "").trim() === "",
                        }
                  )
                }
                placeholder={isPwo ? translate("scene name") : translate("tag id")}
                keyboardType={isPwo ? undefined : "numeric"}
              />
            </Field>
          </View>
        </View>
      ) : null}

      <LessonAssetPicker
        visible={pickerOpen}
        kind={isPwo ? "pwo" : "video"}
        catalog={catalog}
        loading={catalog?.loading}
        error={catalog?.error}
        selectedRef={ref}
        onPick={(picked) => onPatch({ asset_ref: picked })}
        onClose={() => setPickerOpen(false)}
        colors={colors}
        t={t}
      />
    </Field>
  );
};

const styles = StyleSheet.create({
  chosen: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 10,
  },
  chosenTitle: { fontSize: 13, fontWeight: "700" },
  chosenMeta: { fontSize: 11, marginTop: 2 },
  chosenEmpty: { fontSize: 13, fontStyle: "italic" },
  chooseButton: {
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  chooseText: { fontSize: 12, fontWeight: "700" },
  manualToggle: { alignSelf: "flex-start", paddingVertical: 6 },
  manualText: { fontSize: 11, textDecorationLine: "underline" },

  refRow: { flexDirection: "row", gap: 12 },
  refItem: { flex: 1, minWidth: 0 },
});

export default AssetRefField;
